import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import type { Size } from '../../types';
import { DropdownOptionProps } from '../../ui';
import { datasetToAttributes } from '../../utils';
import { Button, ButtonProps } from '../Button';
import { Text } from '../Text';
import {
  StyledCounter,
  StyledError,
  StyledFixedButton,
  StyledInput,
  StyledInputWrapper,
  StyledLabel,
  StyledLabelRequired,
  StyledWrapper,
} from './Input.style';
import type { InputExtensionType } from './Input.type';
import { InputAddonsAfter } from './InputAddonsAfter';
import { InputNumberControllers } from './InputNumber';

export interface InputProps {
  id: string;
  type?:
    | 'text'
    | 'password'
    | 'email'
    | 'number'
    | 'tel'
    | 'url'
    | 'search'
    | 'time'
    | 'date'
    | 'datetime-local';
  required?: boolean;
  name: string;
  size?: Size;
  min?: number;
  max?: number;
  step?: number;
  value?: string | number;
  autocomplete?: boolean;
  placeholder?: string;
  label?: string;
  errorMessage?: string;
  className?: string;
  disabled?: boolean;
  dropdownOptions?: Array<DropdownOptionProps>;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (value: string) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  autofocus?: boolean;
  readOnly?: boolean;
  variant?: 'default' | 'transparent';
  dataset?: Record<string, string>;
  action?: Omit<ButtonProps, 'onClick'> & {
    onClick: (event: React.MouseEvent<HTMLButtonElement>, value: string) => void;
  };
  count?: {
    limit: number;
    min?: number;
    exceedFormatter?: (string: string, options: { limit: number }) => string;
  };
}

const getExtensions = (props: Partial<InputProps>): Array<InputExtensionType> => {
  const { type, dropdownOptions } = props;
  const extensionArray: Array<InputExtensionType> = [];

  if (type === 'number') {
    extensionArray.push('number-controller');
  }

  if (Array.isArray(dropdownOptions) && dropdownOptions.length > 0) {
    extensionArray.push('addons-after');
  }

  return extensionArray;
};

export const Input = React.forwardRef(function Input(
  props: InputProps,
  ref: React.Ref<HTMLInputElement>
): JSX.Element {
  const {
    id,
    type = 'text',
    name,
    size = 'Medium',
    value,
    min,
    max,
    step,
    autocomplete = false,
    onBlur,
    onClick,
    onChange,
    onFocus,
    disabled,
    placeholder,
    className,
    label = '',
    errorMessage = '',
    dropdownOptions = [],
    onKeyDown,
    autofocus = false,
    readOnly = false,
    variant = 'transparent',
    dataset,
    action,
    count,
  } = props;

  const [inputValue, setInputValue] = useState<string>(
    value !== undefined && value !== null ? String(value) : ''
  );
  const [hasExceedLimit, setHasExceedLimit] = useState(false);
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const extensions = getExtensions(props);
  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;

    if (!Number.isNaN(min) && Number(value) < Number(min) && value !== '') {
      value = String(min);
    } else if (!Number.isNaN(max) && Number(value) > Number(max) && value !== '') {
      value = String(max);
    }

    const hasNewValueExceedLimit = count ? value.length > count.limit : false;

    if (hasNewValueExceedLimit && count?.exceedFormatter) {
      value = count.exceedFormatter(value, {
        limit: count.limit,
      });
    }

    setInputValue((previous) => {
      const hasCurrentValueExceedLimit = count ? previous.length > count.limit : false;

      setHasExceedLimit(hasCurrentValueExceedLimit);

      return value;
    });

    onChange?.(value);
  };

  const dataAttributes = useMemo(() => datasetToAttributes(dataset), [dataset]);

  const handleSelect = (value: string) => {
    setInputValue(value);
    onChange?.(value);
  };

  useEffect(() => {
    setInputValue(value !== undefined && value !== null ? String(value) : '');

    if (!count) return;

    setHasExceedLimit(
      (value !== undefined && value !== null ? String(value) : '').length > count.limit
    );
  }, [value, count]);

  return (
    <StyledWrapper>
      {label && (
        <StyledLabel htmlFor={id}>
          <Text size={theme.Form.Input.Size} showTooltip={true}>
            {label}
            {props.required && <StyledLabelRequired> *</StyledLabelRequired>}
          </Text>
        </StyledLabel>
      )}

      <StyledInputWrapper
        $inputSize={size}
        $variant={variant}
        $isError={!!errorMessage || hasExceedLimit}
        $type={type}
        $readOnly={readOnly}
        $disabled={disabled}
      >
        <StyledInput
          {...dataAttributes}
          id={id}
          ref={ref ?? inputRef}
          type={type}
          name={name}
          min={min}
          max={max}
          step={step}
          $inputSize={size}
          className={className}
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          onBlur={onBlur}
          onFocus={onFocus}
          onChange={onChangeHandler}
          $extensions={extensions}
          autoComplete={autocomplete ? 'on' : 'off'}
          aria-autocomplete={autocomplete ? 'list' : 'none'}
          onKeyDown={onKeyDown}
          autoFocus={autofocus}
          onClick={(event) => onClick?.(event)}
          readOnly={readOnly}
        />

        {type === 'number' && (
          <InputNumberControllers
            inputRef={(ref ?? inputRef) as React.RefObject<HTMLInputElement>}
            disabled={disabled}
          />
        )}

        {dropdownOptions.length > 0 && (
          <InputAddonsAfter
            dropdownOptions={dropdownOptions}
            onSelect={(value) => handleSelect(inputValue + value)}
          />
        )}

        {action && (
          <StyledFixedButton>
            <Button
              size={action.size}
              action={action.action}
              variant={action.variant}
              disabled={action.disabled}
              icon={action.icon}
              color={action.color}
              opacity={action.opacity}
              active={action.active}
              textColor={action.textColor}
              shape={action.shape}
              title={action.title}
              type={action.type}
              loading={action.loading}
              onClick={(event) => action?.onClick(event, inputValue)}
            >
              {action?.label}
            </Button>
          </StyledFixedButton>
        )}

        {count && (
          <StyledCounter>
            <Text
              size={'Body'}
              color={
                inputValue.length > count.limit
                  ? theme.Color.Feedback.Alert
                  : theme.Color.Neutrals.Gray
              }
              truncate={true}
            >
              {inputValue.length} / {count.limit}
            </Text>
          </StyledCounter>
        )}
      </StyledInputWrapper>

      {errorMessage && (
        <StyledError>
          <Text size={'Small'} color={theme.Color.Feedback.Alert} showTooltip={true}>
            {errorMessage}
          </Text>
        </StyledError>
      )}
    </StyledWrapper>
  );
});
